import { Input, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Field } from '@/components/ui/field';
import { useCreateProgramAction } from '@/hooks/actions/use-create-affiliate-program-action';

// Simple validation schema for just the name
const createNewProgramSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(3, t('programs.validation.nameMinLength')),
  });

export type CreateNewProgramFormData = {
  name: string;
};

interface CreateNewProgramFormProps {
  onSuccess?: () => void;
  onSubmitting?: (isSubmitting: boolean) => void;
  defaultValues?: Partial<CreateNewProgramFormData>;
  formId: string;
}

const CreateNewProgramForm = ({
  onSuccess,
  onSubmitting,
  defaultValues = {},
  formId,
}: CreateNewProgramFormProps) => {
  const { t } = useTranslation();
  const schema = useMemo(() => createNewProgramSchema(t), [t]);

  const methods = useForm<CreateNewProgramFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const mutation = useCreateProgramAction();

  // Call onSuccess callback when mutation succeeds
  useEffect(() => {
    if (mutation.isSuccess && onSuccess) {
      onSuccess();
    }
  }, [mutation.isSuccess, onSuccess]);

  // Notify parent component about submission state
  useEffect(() => {
    if (onSubmitting) {
      onSubmitting(mutation.isPending);
    }
  }, [onSubmitting, mutation.isPending]);

  const onSubmit = (data: CreateNewProgramFormData) => {
    mutation.mutate(data);
  };

  return (
    <FormProvider {...methods}>
      <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
        <VStack gap={4} align="stretch">
          <Field
            label={t('programs.modals.createProgram.programName')}
            invalid={!!methods.formState.errors.name}
          >
            <Input
              placeholder={t('programs.modals.createProgram.programNamePlaceholder')}
              {...methods.register('name')}
            />
          </Field>
        </VStack>
      </form>
    </FormProvider>
  );
};

export default CreateNewProgramForm;
